import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ShieldCheck, Download, Search, RefreshCw, Calendar } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Admin audit view for compliance acknowledgments.
 * Shows every recorded acknowledgment with who, when, IP, user-agent, and
 * notice version. Supports search and CSV export.
 */
const ComplianceAcknowledgmentsAdmin = ({ adminToken }) => {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentVersion, setCurrentVersion] = useState('');
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(
        `${BACKEND_URL}/api/compliance/admin/acknowledgments`,
        {
          params: { limit: 500, skip: 0, search: search || undefined },
          headers: { Authorization: `Bearer ${adminToken}` },
        }
      );
      setRows(res.data.acknowledgments || []);
      setTotal(res.data.total || 0);
      setCurrentVersion(res.data.current_version || '');
    } catch (err) {
      console.error('Load acknowledgments failed:', err);
      setError(err?.response?.data?.detail || 'Could not load acknowledgments.');
    } finally {
      setLoading(false);
    }
  }, [adminToken, search]);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    load();
  };

  const exportCsv = () => {
    if (!rows.length) return;
    const header = ['acknowledgedAt', 'userEmail', 'userId', 'practiceId', 'version', 'ip', 'userAgent'];
    const escape = (v) => {
      if (v == null) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [header.join(',')]
      .concat(rows.map((r) => header.map((h) => escape(r[h])).join(',')))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `practcom_compliance_acknowledgments_${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Card data-testid="compliance-acks-card">
      <CardHeader className="flex flex-row items-center justify-between gap-2">
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="h-5 w-5 text-emerald-600" />
          Compliance Acknowledgments
          <span className="text-xs font-normal text-gray-500 ml-2">
            Current version: <code className="bg-gray-100 px-1.5 py-0.5 rounded">{currentVersion || '—'}</code>
          </span>
        </CardTitle>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={load}
            data-testid="compliance-acks-refresh"
          >
            <RefreshCw className="h-4 w-4 mr-2" /> Refresh
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={!rows.length}
            data-testid="compliance-acks-export"
          >
            <Download className="h-4 w-4 mr-2" /> Export CSV
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSearch} className="flex gap-2 mb-4">
          <div className="relative flex-1">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search by email, practice ID, or version…"
              className="pl-9"
              data-testid="compliance-acks-search-input"
            />
          </div>
          <Button type="submit" data-testid="compliance-acks-search-btn">
            Search
          </Button>
        </form>

        {error && (
          <div className="mb-3 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm">
            {error}
          </div>
        )}

        <div className="text-xs text-gray-500 mb-2">
          {loading ? 'Loading…' : `${rows.length} shown · ${total} total`}
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="compliance-acks-table">
            <thead>
              <tr className="text-left border-b border-gray-200 text-gray-600 font-semibold">
                <th className="py-2 pr-3">Acknowledged</th>
                <th className="py-2 pr-3">User</th>
                <th className="py-2 pr-3">Practice</th>
                <th className="py-2 pr-3">Version</th>
                <th className="py-2 pr-3">IP</th>
                <th className="py-2 pr-3">User Agent</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 && !loading && (
                <tr>
                  <td colSpan={6} className="py-8 text-center text-gray-400">
                    No acknowledgments recorded yet.
                  </td>
                </tr>
              )}
              {rows.map((r, i) => (
                <tr
                  key={`${r.userId}-${r.version}-${i}`}
                  className="border-b border-gray-100 hover:bg-gray-50"
                  data-testid={`compliance-ack-row-${i}`}
                >
                  <td className="py-2 pr-3 whitespace-nowrap">
                    <div className="flex items-center gap-1.5 text-gray-700">
                      <Calendar className="h-3.5 w-3.5 text-gray-400" />
                      {r.acknowledgedAt
                        ? new Date(r.acknowledgedAt).toLocaleString()
                        : '—'}
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    <div className="font-medium text-gray-900">{r.userEmail || '—'}</div>
                    <div className="text-[11px] text-gray-400 font-mono">{r.userId || ''}</div>
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-gray-500 max-w-[180px] truncate" title={r.practiceId || ''}>
                    {r.practiceId || '—'}
                  </td>
                  <td className="py-2 pr-3">
                    <code className="bg-emerald-50 text-emerald-700 px-1.5 py-0.5 rounded text-[11px]">
                      {r.version}
                    </code>
                  </td>
                  <td className="py-2 pr-3 font-mono text-[11px] text-gray-600">
                    {r.ip || '—'}
                  </td>
                  <td className="py-2 pr-3 text-[11px] text-gray-500 max-w-[280px] truncate" title={r.userAgent || ''}>
                    {r.userAgent || '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </CardContent>
    </Card>
  );
};

export default ComplianceAcknowledgmentsAdmin;
